// The public booking pages' chrome (Phase B2). No app nav, no auth, no
// internal anything — a navy hero band with the wordmark, the phone
// fallback, and the screen's own headline, then the work below it, and
// the human fallback at the foot. Everything inside renders under
// .book-public, which scopes the client-facing palette (book.css).
//
// [2026-09-04] NAVY HERO across the whole flow (Grant: "the design of
// this page is bad… go with the navy hero, build it across the whole
// booking flow"). Every screen used to open on a bare centered H1 over
// the light-gray page; the advisor-choice step in particular reused the
// small topic-tile grid, so credentials wrapped to four lines and names
// ran off their cards. Now every page hands its headline to the shell
// (`hero`), the shell paints it on the brand's own hero treatment —
// navy gradient, grain, glow orbs, yellow swoosh on the last word — and
// the first card of each step lifts out of the band. One shell, one
// treatment: a step can't drift back to the old look by forgetting to
// style its own H1.

import { useState } from "react";
import type { ReactNode } from "react";

import { Check } from "lucide-react";

import { ClientShell } from "../client-shell/ClientShell";

import "./book.css";

/** Where the client is in the four-step routed flow (Topic · Who ·
 *  When · Details). "done" lights every step; omit it on screens that
 *  aren't part of that flow (pinned personal pages, invite links,
 *  manage links, dead ends). */
export type BookProgress = 1 | 2 | 3 | 4 | "done";

export interface BookHero {
  /** Small light-blue caps line above the title — the topic the client
   *  picked, carried through the flow as the "you're in the right
   *  place" thread. Rendered with a check mark. */
  eyebrow?: ReactNode;
  /** The page's one H1. Plain text: the last word gets the swoosh. */
  title: string;
  /** A bold white line under the title — the booked day and time on a
   *  confirmation, nothing on most screens. */
  kicker?: ReactNode;
  lede?: ReactNode;
  /** A quiet white link under the lede ("← Different topic"). */
  back?: ReactNode;
  progress?: BookProgress | undefined;
  /**
   * The step labels the dots stand for. Defaults to the front door's four
   * (Topic · Who · When · Details); the connected experience passes its
   * own ("When · Questions · Done" / "Questions · When · Done"), since a
   * personal link has no topic or who step to show (2026-09-05).
   */
  steps?: readonly string[] | undefined;
}

const STEP_LABELS = ["Topic", "Who", "When", "Details"] as const;

function ProgressSteps({
  progress,
  steps = STEP_LABELS,
}: {
  progress: BookProgress;
  steps?: readonly string[] | undefined;
}) {
  const current = progress === "done" ? steps.length + 1 : progress;
  return (
    <ol className="book-steps" aria-label="Booking progress">
      {steps.map((label, i) => {
        const n = i + 1;
        const state = n < current ? "done" : n === current ? "now" : "todo";
        return (
          <li
            key={label}
            className={`book-step-item book-step-item--${state}`}
            aria-current={state === "now" ? "step" : undefined}
          >
            <span className="book-step-dot" aria-hidden="true">
              {state === "done" ? <Check size={12} strokeWidth={2.5} /> : n}
            </span>
            <span className="book-step-label">{label}</span>
          </li>
        );
      })}
    </ol>
  );
}

/** The H1 with the yellow swoosh under its last word — the brand's
 *  "exclamation point," used once per page and nowhere else. */
function SwooshTitle({ title }: { title: string }) {
  const words = title.trim().split(/\s+/);
  const last = words.pop() ?? "";
  return (
    <>
      {words.length > 0 ? `${words.join(" ")} ` : ""}
      <span className="cs-sw">{last}</span>
    </>
  );
}

export function BookShell({
  children,
  hero,
  wide,
}: {
  children?: ReactNode;
  /** The screen's headline, painted on the navy band. Every screen has
   *  one — a booking page with no H1 has no way to say where the client
   *  is. */
  hero: BookHero;
  /** The front door's booking flow: a wider card column on a computer. */
  wide?: boolean;
}) {
  // Since lane 2.3 of the design program (2026-11-05) the booking pages
  // wear the ONE client shell (../client-shell/ClientShell): the hero
  // carries the step dots, the topic eyebrow, the title with its swoosh,
  // the kicker and lede, and the back link; the cards sit in the shell's
  // column. Every step's own markup is unchanged; book.css takes the
  // standard's values. "book-public" stays on the root so book.css's
  // scoped values reach both the hero and the cards.
  return (
    <ClientShell
      className="book-public"
      wide={wide}
      heroTop={hero.progress ? <ProgressSteps progress={hero.progress} steps={hero.steps} /> : undefined}
      eyebrow={hero.eyebrow}
      title={<SwooshTitle title={hero.title} />}
      lead={
        hero.kicker || hero.lede ? (
          <>
            {hero.kicker && <div className="book-hero-kicker">{hero.kicker}</div>}
            {hero.lede}
          </>
        ) : undefined
      }
      heroBottom={hero.back ? <div className="book-hero-back">{hero.back}</div> : undefined}
    >
      <div className="book-body">{children}</div>
    </ClientShell>
  );
}

/** Initials avatar when no photo exists, or when one fails to load —
 *  never a broken-image icon. */
export function AdvisorAvatar({
  name,
  photoUrl,
}: {
  name: string;
  photoUrl: string | null;
}) {
  const [failed, setFailed] = useState(false);
  if (photoUrl && !failed) {
    return (
      <img
        className="book-avatar"
        src={photoUrl}
        alt=""
        onError={() => setFailed(true)}
      />
    );
  }
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0] ?? "")
    .join("")
    .toUpperCase();
  return (
    <div className="book-avatar" aria-hidden="true">
      {initials || "S"}
    </div>
  );
}
